/**
 * PF Revamp — DraftStore interface + pure helpers.
 *
 * v1 implementation is `LocalDraftStore` (localStorage-backed, single tab).
 * v2 will replace the singleton with a BE-backed adapter; the interface stays
 * the same so `purchaseFlowDrafts.query.ts` hooks and the auto-save
 * subscription remain untouched.
 *
 * See master plan §4 + `decisions.md#seed-08` (local-first, BE later).
 */

import type { DraftId, OrderDraftDTO } from 'models/purchaseFlowRevamp'

/** A draft not saved for this many days is deleted from the device. */
export const DRAFT_RETENTION_DAYS = 14

/** Lightweight metadata row — shown in the "Continue Order" popup list. */
export interface DraftMeta {
  draftId: DraftId
  /** Property's formatted address, or `null` if no property picked yet. */
  propertyAddress: string | null
  /** Sum of cart-item quantities. */
  itemCount: number
  /** Names of the products in the cart, once each. Empty for lines saved before names were recorded. */
  productNames?: string[]
  /** The purchase session the draft was saved in, so a reload of that session's URL can find it. */
  sessionId?: string
  /** Epoch-ms timestamp of the last save. */
  updatedAt: number
  /** Epoch-ms timestamp of initial creation. */
  createdAt: number
  /**
   * PF Revamp FE version at the time this draft was last saved. Mirrors
   * `OrderDraftDTO.pfVersion`. Stored here so `LocalDraftStore.list` can
   * filter mismatched entries without reading each draft body.
   */
  pfVersion: number
  /**
   * W-22 — platform user id of the signed-in user who last saved this draft.
   * Mirrors `OrderDraftDTO.ownerUserId` so `list()` can scope rows without
   * reading each body. `undefined` for pre-W-22 (legacy) drafts.
   */
  ownerUserId?: string
  /**
   * W-22b — the workspace the draft was started in. Mirrors
   * `OrderDraftDTO.ownerWorkspaceId`, and is deliberately NOT the DTO's
   * admin-only `workspaceId` (the on-behalf-of target). `undefined` for drafts
   * written before this existed, and for a viewer with no workspace of their own.
   */
  ownerWorkspaceId?: string
}

/**
 * Who is looking, and from where. Threaded in by the caller so the store stays
 * pure; both fields are optional because either can still be resolving.
 */
export interface DraftScope {
  ownerUserId?: string
  /** The viewer's currently selected workspace (`currentUserWorkspace.id`). */
  workspaceId?: string
}

/**
 * Pluggable persistence contract. Local implementation today, BE tomorrow.
 *
 * The `ownerUserId` arguments are threaded in by the caller (the
 * `purchaseFlowDrafts.query` hooks, which read `useUserData`) so the store
 * implementation stays pure — no auth/context imports below this line. They
 * are optional because the id can still be unresolved while `/user/me` is in
 * flight; see `isDraftVisibleTo` for how that case is handled.
 */
export interface DraftStore {
  /** Lists drafts **visible to** `scope` — see `isDraftVisibleTo`. */
  list(scope?: DraftScope): Promise<DraftMeta[]>
  get(draftId: DraftId): Promise<OrderDraftDTO | null>
  /** Persists a body and (re)stamps the scope on body + index entry. */
  save(draftId: DraftId, dto: OrderDraftDTO, scope?: DraftScope): Promise<void>
  /** Creates a fresh draft row owned by `scope`, returns the new draftId. */
  create(scope?: DraftScope): Promise<DraftId>
  delete(draftId: DraftId): Promise<void>
}

/**
 * W-22 — draft visibility rule for a shared browser.
 *
 * - Owned draft → visible only to that owner. A second account signing into the
 *   same browser must not see it.
 * - Unowned (legacy, pre-W-22) draft → visible to everyone. Deliberately
 *   fail-open: those bodies carry no owner to compare against and deleting or
 *   hiding them would destroy resumable work. They become owned on their first
 *   save (`LocalDraftStore.save` re-stamps), and otherwise age out on the
 *   30-day purge.
 * - Unresolved viewer (`ownerUserId === undefined`, e.g. `/user/me` still in
 *   flight) → owned drafts stay hidden. Fails **closed**: an unknown viewer is
 *   never handed another account's drafts. The list re-runs once the id
 *   resolves because it is part of the React Query key.
 *
 * W-22b adds the same rule on the workspace axis — see the inline comment.
 */
export function isDraftVisibleTo(
  meta: Pick<DraftMeta, 'ownerUserId' | 'ownerWorkspaceId'>,
  scope: DraftScope = {}
): boolean {
  if (meta.ownerUserId && meta.ownerUserId !== scope.ownerUserId) return false
  // W-22b — a draft belongs to the workspace it was started in: switching
  // workspace must not surface another workspace's order. Applied only when both
  // sides know their workspace: a draft written before this existed has none to
  // compare, and an admin has no property workspace of their own, so in either
  // case the owner rule above is the whole rule (same fail-open reasoning as the
  // legacy unowned drafts).
  if (meta.ownerWorkspaceId && scope.workspaceId && meta.ownerWorkspaceId !== scope.workspaceId) return false
  return true
}

/**
 * Derives the `DraftMeta` fields (minus `draftId`) from an `OrderDraftDTO`.
 *
 * The DTO already carries `createdAt`/`updatedAt` as ISO strings. We parse
 * them into epoch ms for list-sorting without touching the wire format.
 */
export function deriveDraftMeta(dto: OrderDraftDTO): Omit<DraftMeta, 'draftId'> {
  const propertyAddress = dto.property?.addressLabel ?? null

  let itemCount = 0
  for (const item of dto.items ?? []) {
    itemCount += item.quantity
  }

  // A pack is named once, by its own name, rather than by the products it holds.
  const productNames = [...new Set((dto.items ?? [])
    .map((item) => item.pack?.name ?? item.productName)
    .filter((name): name is string => !!name))]

  return {
    propertyAddress,
    itemCount,
    productNames,
    sessionId: dto.session?.id,
    updatedAt: Date.parse(dto.updatedAt) || 0,
    createdAt: Date.parse(dto.createdAt) || 0,
    pfVersion: dto.pfVersion ?? 0,
    // W-22 — mirrors the body's owner onto the index row. `undefined` for
    // legacy bodies written before ownership existed.
    ownerUserId: dto.ownerUserId,
    // W-22b — same, on the workspace axis.
    ownerWorkspaceId: dto.ownerWorkspaceId,
  }
}

/**
 * M8 / QA-539 — a draft with no cart items is "empty": there is nothing to resume,
 * since the property alone is picked again in one step, and it would surface as
 * a meaningless "Empty cart" row.
 *
 * Empty drafts are created eagerly (the entry "Start new order" action and the
 * auto-save subscription both mint a draftId before the user has added a
 * product). The saved-orders list and the entry "Continue draft" gate filter
 * these out so a freshly-abandoned or just-emptied order doesn't linger as a
 * ghost card.
 */
export function isEmptyDraft(meta: DraftMeta): boolean {
  return meta.itemCount === 0
}
